import classNames from 'classnames'
import { useToasts } from '../../../../../components/complex/Toast'
import { t } from '../../../../../utils'
import { StoreItem, StoreSample } from '../../../../../__generated__/graphql'
import styles from './_keyButtons.module.scss'
import { useMutation } from '@apollo/client'
import { DOWNLOAD_STORE_FILE_MUTATION } from '../../../../../gql'
import { useState } from 'react'

const keyMapping = {
  C: 'C',
  'C#': 'C#/Db',
  Db: 'C#/Db',
  D: 'D',
  'D#': 'D#/Eb',
  Eb: 'D#/Eb',
  E: 'E',
  F: 'F',
  'F#': 'F#/Gb',
  Gb: 'F#/Gb',
  G: 'G',
  Ab: 'G#/Ab',
  'G#': 'G#/Ab',
  A: 'A',
  Bb: 'A#/Bb',
  'A#': 'A#/Bb',
  B: 'B'
}

const keys = [
  'A',
  'A#/Bb',
  'B',
  'C',
  'C#/Db',
  'D',
  'D#/Eb',
  'E',
  'F',
  'F#/Gb',
  'G',
  'G#/Ab'
]
interface KeyButtonsProps {
  storeItem: StoreItem
  downloadOnClick?: boolean
}

const orderPayloads = (
  payloads: StoreSample[],
  keys: string[]
): StoreSample[] => {
  return payloads.sort((a, b) => {
    return (
      keys.indexOf(keyMapping[a.key] || a.key) -
      keys.indexOf(keyMapping[b.key] || b.key)
    )
  })
}

const areEqualKeys = (firstKey: string, secondKey: string) => {
  const first = keyMapping[firstKey] || firstKey
  const second = keyMapping[secondKey] || secondKey
  return first == second
}

const KeyButtons: React.FC<KeyButtonsProps> = ({
  storeItem,
  downloadOnClick = false
}) => {
  const isOrigignalKey = (payload) => {
    const songKey = storeItem.song.key
    const payloadKey = payload.key
    return areEqualKeys(songKey, payloadKey)
  }

  const payloadsWithSlash = storeItem.payloads.map((payload) => {
    return {
      ...payload,
      key: payload.key
        ?.match(/[ABCDEFGb\/#:]+/gm)[0]
        .replace(':', '/')
        .replace(' - Original', '')
        .trim() //417
    }
  })

  const originalPayload = payloadsWithSlash.find(isOrigignalKey)

  const orderKeys = (originalKey: string): string[] => {
    const originalIndex = keys.indexOf(originalKey)
    const result = []

    for (let i = keys.length / 2; i >= 1; i--) {
      const index =
        originalIndex - i >= 0
          ? originalIndex - i
          : keys.length - (i - originalIndex)
      result.push(keys[index])
    }

    result.push(originalKey)

    for (let i = 1; i <= keys.length / 2 - 1; i++) {
      const index =
        originalIndex + i < keys.length
          ? originalIndex + i
          : Math.abs(keys.length - (i + originalIndex))
      result.push(keys[index])
    }
    return result
  }

  const orderedPayloads = orderPayloads(
    payloadsWithSlash,
    orderKeys(originalPayload?.key || storeItem.song.key)
  )

  if (!orderedPayloads.length) return

  return (
    <>
      {orderedPayloads.map((payload) => {
        const isOriginalKey = payload.id === originalPayload?.id
        const initialPayload = storeItem.payloads.find(
          (initPayload) => initPayload.id === payload.id
        )
        return (
          <li
            key={payload.id}
            className={`column ${isOriginalKey ? 'is-12' : 'is-4'}`}
          >
            <KeyButton
              payload={payload}
              initialPayload={initialPayload}
              isOriginalKey={isOriginalKey}
              downloadOnClick={downloadOnClick}
              storeItemId={storeItem.id}
            />
          </li>
        )
      })}
    </>
  )
}

const KeyButton = ({
  storeItemId,
  payload,
  initialPayload,
  isOriginalKey,
  downloadOnClick
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const [downloadStoreFile] = useMutation(DOWNLOAD_STORE_FILE_MUTATION)

  const { addToast } = useToasts()

  const handleClick = (payload) => {
    if (downloadOnClick) {
      setIsLoading(true)
      downloadStoreFile({
        variables: { id: storeItemId, key: payload.key },
        onCompleted: (data) => {
          const url = data.downloadStoreFile.url
          const link = document.createElement('a')
          link.href = url
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setIsLoading(false)
        },
        onError: () => {
          setIsLoading(false)
          addToast(t('Error downloading file'), 'error')
        }
      })
    }
  }
  return (
    <button
      onClick={() => handleClick(initialPayload)}
      className={classNames(styles.storeSongKeyButton, {
        [styles.isOriginalKey]: isOriginalKey,
        [styles.buttonHover]: downloadOnClick,
        [styles.buttonDisabled]: isLoading
      })}
    >
      {isOriginalKey && <span>{t('Original Key:')}</span>}
      {payload.key}
    </button>
  )
}

export default KeyButtons
